import { Dialog, DialogContent, CircularProgress } from "@mui/material";
import styled from "@emotion/styled";
import { fromPublic, replaceGlobalConfigVars } from "../../utils";
import { useEffect, useState, useCallback } from "react";
import parse from "html-react-parser";

interface CompetitionDialogProps {
  open: boolean;
  loading?: boolean;
  success: boolean;
  onClose: () => void;
  loadingMessage?: string;
  successMessage?: string;
  errorMessage?: string;
}

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    margin: 0;
    max-width: none;
    width: 290px;
    min-height: 314px;
    background-color: #F1F1F1;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  
  .MuiDialogContent-root {
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .MuiBackdrop-root {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const ContentContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 24px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  text-align: center;
`;

const StyledImage = styled.img`
  width: 80px;
  height: 80px;
`;

interface FallbackIconProps {
  color?: string;
  backgroundColor?: string;
}

const FallbackIcon = styled.div<FallbackIconProps>`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: ${'#ffffff'};
  background-color: ${'#14a248'};
  border-radius: 50%;
`;


const StyledCircularProgress = styled(CircularProgress)`
  color: #616161;
  width: 80px !important;
  height: 80px !important;
`;

const Message = styled.div`
  white-space: pre-line;
  
  a {
    color: #14a248;
    text-decoration: underline;
    font-weight: 600;
  }
`;

const DIALOG_CONTENT = {
  success: {
    message: "You're all set!\n\nYour entry into the competition has been confirmed.\n\nBest of luck!",
    icon: `${process.env.PUBLIC_URL}/custom/common/success.svg`, 
    fallbackText: "✓" 
  },
  error: {
    message: "Sorry, there was an error entering the competition.\n\nPlease try again.",
    icon: `${process.env.PUBLIC_URL}/custom/common/error.svg`,
    fallbackText: "✗" 
  },
  loading: {
    message: "Entering competition..."
  }
};

interface DialogIconProps {
  loading: boolean;
  success: boolean;
  imageFailed: boolean;
  setImageFailed: (failed: boolean) => void;
}

const DialogIcon = ({ 
  loading, 
  success, 
  imageFailed, 
  setImageFailed
}: DialogIconProps) => {
  if (loading) {
    return <StyledCircularProgress />;
  }
  
  if (imageFailed) {
    const fallbackText = success ? 
      DIALOG_CONTENT.success.fallbackText : 
      DIALOG_CONTENT.error.fallbackText;
    
    const iconColor = success ? "#14a248" : "#d32f2f";
    
    return (
      <FallbackIcon color={iconColor}>
        {fallbackText}
      </FallbackIcon>
    );
  }
  
  const iconPath = success ? 
    DIALOG_CONTENT.success.icon : 
    DIALOG_CONTENT.error.icon;
  
  const baseUrl = window.location.origin;
  const fullIconPath = `${baseUrl}${fromPublic(iconPath)}`;
  
  const handleImageError = () => {
    console.warn("Image failed to load, using fallback icon");
    setImageFailed(true);
  };
  
  return (
    <StyledImage 
      src={fullIconPath}
      alt={success ? "Success icon" : "Error icon"} 
      onError={handleImageError}
      key={`dialog-icon-${success ? 'success' : 'error'}`}
    />
  );
};

const CompetitionDialog = ({ 
  open, 
  loading = false, 
  success, 
  onClose,
  loadingMessage,
  successMessage,
  errorMessage 
}: CompetitionDialogProps) => {
  // State for image loading - we'll track if the image has failed to render
  const [imageFailed, setImageFailed] = useState(false);
  
  // Listen for visibility change events to handle app switching
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // App has become visible again, reset image failed state
        console.log('App became visible - refreshing images');
        setImageFailed(false);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // Reset image states when dialog opens or success state changes
  useEffect(() => {
    if (open) {
      setImageFailed(false);
    }
  }, [open, success]);
  
  const loadImage = useCallback((url: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        setImageFailed(false);
        try {
          sessionStorage.setItem(
            `competition-dialog-image-${success ? 'success' : 'error'}`, 
            'loaded'
          );
          console.log('Successfully stored image status in sessionStorage');
        } catch (e) {
          console.warn('Failed to store image status in sessionStorage', e);
        }
        resolve();
      };
      
      img.onerror = (error) => {
        reject(new Error(`Failed to load image: ${url}`));
      };
      
      img.src = url;
    });
  }, [success]);

  const retryImageLoad = useCallback(async (url: string, retries = 3, delayMs = 500): Promise<void> => {
    try {
      await loadImage(url);
    } catch (error) {
      if (retries > 0) {
        console.warn(`Retrying to load image (${retries} attempts left): ${url}`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
        return retryImageLoad(url, retries - 1, delayMs);
      }
      setImageFailed(true);
      throw error;
    }
  }, [loadImage]);
  
  useEffect(() => {
    if (!open) return; 
    const preloadImage = async () => {
      const iconPath = success ? DIALOG_CONTENT.success.icon : DIALOG_CONTENT.error.icon;
      const absoluteUrl = `${window.location.origin}${fromPublic(iconPath)}`;
      
      try {
        await retryImageLoad(absoluteUrl);
      } catch (error) {
        console.error("Failed to preload dialog images after multiple attempts:", error);
        setImageFailed(true);
      }
    };
    
    preloadImage();
    
  }, [open, success, retryImageLoad]);


  let message: string;
  if (loading) {
    message = loadingMessage || DIALOG_CONTENT.loading.message;
  } else if (success) {
    message = successMessage || DIALOG_CONTENT.success.message;
  } else {
    message = errorMessage || DIALOG_CONTENT.error.message;
  }
  
  const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (success || loading) {
      return;
    }
    onClose();
  };

  return (
    <StyledDialog 
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={success || loading}
      aria-labelledby="competition-dialog-title"
    >
      <DialogContent>
        <ContentContainer>
          <DialogIcon
            loading={loading}
            success={success}
            imageFailed={imageFailed}
            setImageFailed={setImageFailed}
          />
          <Message>
            {parse(replaceGlobalConfigVars(message))}
          </Message>
        </ContentContainer>
      </DialogContent>
    </StyledDialog>
  );
};

export default CompetitionDialog;